import {Button, Icon, NonIdealState} from "@blueprintjs/core";
import {IconNames} from "@blueprintjs/icons";
import {
    FGCheckboxInput,
    FGCustomPanel,
    FGEmpty,
    FGListen,
    FGNumberInput,
    FGSelectInput,
    FGTextAreaInput,
    FGTextInput,
    FieldGroup,
    FieldSet,
    InlineButtonContainer,
    useApiEffect,
} from "nsitools-react";
import * as React from "react";
import {useHistory} from "react-router";
import * as Yup from "yup";

import {
    ClubApi,
    CompetitionRequestApi,
    CompetitionRequestDto,
    CountryApi,
    ERequestState,
    PersonApi,
    RequestApi,
    RequestDto,
} from "../../../../api";
import {
    ButtonsBloc,
    CancelButton,
    FGCountrySelect,
    FGPeopleSearchInput,
    FGSRSHCheckboxInput,
    FGSRSHDateMaskInput,
    SharepointButton,
    SmallFormGenerator,
    ToolTipContainer,
} from "../../../../components";
import {useDialog, useEventsContext} from "../../../../contexts";
import {ERoutes} from "../../../../ERoutes";
import {useApiService, useCrudApi, useTl, useWindow} from "../../../../hooks";
import {useReferential} from "../../../../hooks/useReferential";
import {ETLCodes} from "../../../../locales";
import {CompetitionJudgeSelection} from "./CompetitionJudgeSelection";
import {FormikProps} from "formik";
import {Popover2} from "@blueprintjs/popover2";

export interface ICompetitionRequestDetailProps {
    competitionRequestId: number;
    editMode: boolean;
}

export const CompetitionRequestDetail: React.FunctionComponent<ICompetitionRequestDetailProps> = ({
                                                                                                      competitionRequestId,
                                                                                                      editMode,
                                                                                                  }) => {
    const [clubRemark, setclubRemark] = React.useState("");
    const {t} = useTl();
    const history = useHistory();
    const {openNewTab} = useWindow();
    const {showDialogPromise} = useDialog();
    const {dispatchEvent} = useEventsContext();

    const api = useApiService(CompetitionRequestApi);
    const countryApi = useApiService(CountryApi);
    const clubApi = useApiService(ClubApi);
    const personApi = useApiService(PersonApi);

    const [judgeIdsBeforeEdition, setJudgeIdsBeforeEdition] = React.useState([]);
    const [sectionId, setSectionId] = React.useState<number>();
    const [activityId, setActivityId] = React.useState<number>();
    const [clubId, setClubId] = React.useState<number>();
    const [isBelgium, setIsBelgium] = React.useState<boolean>(true);
    const [otherAdress, setOtherAdress] = React.useState<boolean>(false);

    const [clubs, cloading] = useReferential((a) => a.getClubs(null), true, []);
    const [sections, sloading] = useReferential((a) => a.getSections(), true, []);
    const [activities, aloading] = useReferential((a) => a.getActivities(sectionId), true, [sectionId]);
    const [subActivities, saloading] = useReferential((a) => a.getSubActivities(activityId), true, [activityId]);
    const [postalCodes, pcloading] = useReferential((a) => a.getPostalCodes(), true, []);
    const [volunteerProfiles, volunteerProfilesLoading] = useReferential((a) => a.getVolunteerProfiles(), true, []);

    //===================== Getting Data ===========================
    const [belgiumCountryId] = useApiEffect(async () => await countryApi.getBelgianId(), [countryApi]);

    const {data, loading, saving, saveItem, refresh} = useCrudApi<CompetitionRequestDto>(
        React.useMemo(
            () => ({
                getApiFn: async (id = competitionRequestId) => {
                    if (id > 0) {
                        var data = await api.get(id);
                        checkClubBelium(data?.clubId, null);
                        return data;
                    } else {
                        return new CompetitionRequestDto({
                            judgeIds: [],
                            competitionVolunteerProfiles: [],
                            request: new RequestDto({
                                requestDate: new Date(),
                                status: ERequestState.REQUESTED,
                                remark: "",
                            }),
                        }).toJSON();
                    }
                },
                saveApiFn: async (d: CompetitionRequestDto) => {
                    if (
                        data?.competitionRequestId > 0 &&
                        (d.dateFrom.getTime() !== data?.dateFrom.getTime() || d.dateTo.getTime() !== data?.dateTo.getTime())
                    ) {
                        const dialogReEs = await showDialogPromise({
                            message: t(ETLCodes.IfDatesAreUpdatedAllConfiguredExpoWillBeLostDoYouWantToContinue),
                            title: t(ETLCodes.Warning),
                        });
                        if (dialogReEs !== "yes") return;
                    }
                    // Normaliser les dates à midi pour éviter les problèmes de timezone UTC
                    const normalizeToNoon = (date: Date) =>
                        new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
                    const dataToSave = CompetitionRequestDto.fromJS({
                        ...d,
                        dateFrom: d.dateFrom ? normalizeToNoon(d.dateFrom) : d.dateFrom,
                        dateTo: d.dateTo ? normalizeToNoon(d.dateTo) : d.dateTo,
                    });
                    const id = await api.save(dataToSave);
                    const updatedJudgeIds = [...judgeIdsBeforeEdition];
                    d.judgeIds?.forEach((judgeId) => {
                        if (!updatedJudgeIds.includes(judgeId)) {
                            updatedJudgeIds.push(judgeId);
                        }
                    });
                    setJudgeIdsBeforeEdition(updatedJudgeIds);
                    dispatchEvent("RefetchRequestStatuses");
                    await api.createCompetition(id);
                    return id;
                },
                onSavedRoute: (d) => `${ERoutes.competitionRequest}/${d.competitionRequestId}/detail/edit`,
            }),
            [api, competitionRequestId, judgeIdsBeforeEdition]
        ),
        [competitionRequestId]
    );

    const [saveChecking, setSaveChecking] = React.useState(false);
    const saveCheck = React.useCallback(
        async (values) => {
            setSaveChecking(true);
            let isBanned = false;
            if (competitionRequestId <= 0) {
                isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
            }
            if (isBanned) {
                const isbannedValidation = await showDialogPromise({
                    message: t(ETLCodes.PersonIsBannedConfirmMessage),
                    title: t(ETLCodes.Warning),
                });
                if (isbannedValidation !== "yes") return;
            }
            setSaveChecking(false);
            await saveItem(values);
        },
        [competitionRequestId, personApi, saveItem, showDialogPromise, t]
    );

    //======================== Data Manipulation ==============================
    const statusEditable = React.useMemo(() => {
        if (data) {
            if (data.request.status) {
                return data.request.status === ERequestState.REQUESTED;
            } else {
                return true;
            }
        }
        return false;
    }, [data]);

    const RequestedFormSchema = React.useMemo(() => {
        return Yup.object().shape({
            clubId: Yup.number()
                .nullable()
                .required(t(ETLCodes.Required))
                .test("is-not-excluded", t(ETLCodes.ClubIsExcluded), async function (value) {
                    if (competitionRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                        const result = await clubApi.isClubExcluded(value);
                        return !result.value;
                    }
                    return true;
                }),
            clubEmail: Yup.string().nullable().required(t(ETLCodes.Required)),
            // competitionVolunteerProfiles: Yup.array()
            //   .nullable()
            //   .test("max-duration-test", t(ETLCodes.Required), function (value: EVolunteerProfile[]) {
            //     return !this.parent.myDogShowAuthorized || (!!value && value?.length > 0);
            //   }),
            dateFrom: Yup.date()
                .nullable()
                .required(t(ETLCodes.Required))
                .test("max-duration-test", t(ETLCodes.MaxDurationTwoDays), function (value: Date) {
                    if (this.parent.dateTo && value) {
                        const diffTime = Math.abs(this.parent.dateTo?.getTime() - value?.getTime());
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return diffDays <= 2;
                    }
                    return true;
                })
                .test("date-test", t(ETLCodes.DateMustBeSmallerThanEndDate), function (value: Date) {
                    if (this.parent.dateTo && value) {
                        return value <= this.parent.dateTo;
                    }
                    return true;
                }),
            dateTo: Yup.date()
                .nullable()
                .required(t(ETLCodes.Required))
                .test("date-test", t(ETLCodes.DateMustBeGreaterThanStartDate), function (value: Date) {
                    if (this.parent.dateFrom && value) {
                        return value >= this.parent.dateFrom;
                    }
                    return true;
                }),
            sectionId: Yup.number()
                .nullable()
                .required(t(ETLCodes.Required))
                .test("is-part-of-club", t(ETLCodes.SectionIsNotPartOfClubError), async function (value) {
                    if (value && this.parent.clubId) {
                        const result = await api.competitionSectionCheck(this.parent.clubId, value);
                        return result.value;
                    }
                    return true;
                }),
            activityId: Yup.number().nullable().required(t(ETLCodes.Required)),
            subActivityId: Yup.number().nullable().required(t(ETLCodes.Required)),
            countryId: Yup.number()
                .nullable()
                .test("is-required", t(ETLCodes.Required), async function (value) {
                    if (this.parent.otherAddress) {
                        return !!value;
                    }
                    return true;
                }),
            postalCodeId: Yup.number()
                .nullable()
                .test("is-required", t(ETLCodes.Required), async function (value) {
                    if (this.parent.countryId && isBelgium) {
                        return !!value;
                    }
                    return true;
                }),
            competitionName: Yup.string()
                .nullable()
                .test("is-name-required", t(ETLCodes.Required), async function (value) {
                    return !this.parent.myDogShowAuthorized || !!value;
                }),
            foreignCity: Yup.string()
                .nullable()
                .test("is-required", t(ETLCodes.Required), async function (value) {
                    if (this.parent.countryId && !isBelgium && this.parent.otherAddress) {
                        return !value;
                    }
                    return true;
                }),
            request: Yup.object().shape({
                personId: Yup.number()
                    .nullable()
                    .required(t(ETLCodes.Required))
                    .test("is-of-club", t(ETLCodes.PersonIsNotOfClub), async function (value) {
                        if (clubId) {
                            const res = await api.competitionPersonCheck(clubId, value);
                            return res.value;
                        }
                        return true;
                    })
                    .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
                        if (competitionRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                            if (value !== 0 && value !== null && value !== undefined) {
                                const result = await personApi.missingAddressInformationCheck(value);
                                return result.value;
                            }
                            return true;
                        }
                        return true;
                    }),
                requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
            }),
        });
    }, [api, clubApi, clubId, competitionRequestId, data, isBelgium, personApi, t]);

    const OtherFormSchema = React.useMemo(() => {
        return Yup.object().shape({
            catalogReceptionDate: Yup.date()
                .nullable()
                .test("is-required", t(ETLCodes.Required), async function (value) {
                    if (data?.request?.status === ERequestState.AWAITING_CATALOG) {
                        return !!value;
                    }
                    return true;
                }),
            numberParticipants: Yup.number().test("is-required", t(ETLCodes.Required), async function (value) {
                if (data?.request?.status === ERequestState.AWAITING_CATALOG) {
                    return !!value;
                }
                return true;
            }),
        });
    }, [data, t]);

    const onCancel = React.useCallback(() => history.push(`${ERoutes.competitionRequest}`), [history]);

    //REFRESH THE REQUEST ON STATE CHANGE
    const onRequestRefreshEvent = React.useCallback(() => {
        refresh();
    }, [refresh]);

    const {subscribeToEvent, unsubscribeEvent} = useEventsContext();
    React.useEffect(() => {
        subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
        return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
    }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

    const requestApi = useApiService(RequestApi);
    const [shpLoading, setShpLoading] = React.useState(false);
    const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
    const openOrCreateInShp = React.useCallback(async () => {
        setShpLoading(true);
        if (!!data?.request.sharepointUri) {
            window.open(data?.request.sharepointUri, "_blank");
            setShpLoading(false);
        } else {
            try {
                await requestApi.createInSharepoint(data?.requestId);
                refresh();
            } catch (e) {
                throw e;
            } finally {
                setShpLoading(false);
            }
        }
    }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

    const [clubShpLoading, setClubShpLoading] = React.useState(false);
    const clubExistsInShp = React.useMemo(() => !!data?.clubSharepointUri, [data?.clubSharepointUri]);
    const openOrCreateClubInShp = React.useCallback(async () => {
        setClubShpLoading(true);
        if (!!data?.clubSharepointUri) {
            window.open(data?.clubSharepointUri, "_blank");
            setClubShpLoading(false);
        } else {
            try {
                await clubApi.createInSharepoint(data?.clubId);
                refresh();
            } catch (e) {
                throw e;
            } finally {
                setClubShpLoading(false);
            }
        }
    }, [data?.clubSharepointUri, data?.clubId, clubApi, refresh]);

    //============================= Form ==========================
    const getIsBelgium = React.useCallback(
        async (countryId: number) => {
            if (countryId) {
                const res = await countryApi.isBelgian(countryId);
                return res.value;
            }
        },
        [countryApi]
    );
    const checkIsBelgium = React.useCallback(
        async (countryId: number) => {
            if (countryId) {
                const res = await countryApi.isBelgian(countryId);
                setIsBelgium(res.value);
            }
        },
        [countryApi]
    );
    const formikRef = React.useRef<FormikProps<CompetitionRequestDto>>();

    const [belgianClub, setbelgianClub] = React.useState(false);

    const checkClubBelium = React.useCallback(async (clubId: number, formik: FormikProps<CompetitionRequestDto>) => {
        if (formik?.values?.otherAddress) {
            setbelgianClub(true);
            return;
        }
        if (!clubId) {
            setbelgianClub(false);
        }
        const club = await clubApi.get(clubId);
        const isBelgium = await getIsBelgium(club.countryId);
        console.log("isclubbelian", isBelgium);
        if (isBelgium) {
            setbelgianClub(true);
        } else {
            setbelgianClub(false);
            formik?.setFieldValue("myDogShowAuthorized", false);
        }
    }, []);

    const [myDogShowAuthorized, setMyDogShowAuthorized] = React.useState(false);

    const onDogShowUpdated = React.useCallback(
        (value: boolean, formik: FormikProps<any>) => {
            setMyDogShowAuthorized(value);
            formik?.setFieldValue("crufts", false);
            formik?.setFieldValue("shouldDouble", false);
        },
        [setMyDogShowAuthorized]
    );

    React.useEffect(() => {
        if (!!activities && activities.length == 1) {
            formikRef.current.setFieldValue("activityId", +activities[0].value);
        }
    }, [activities]);

    React.useEffect(() => {
        setclubRemark(data?.clubRemark);
    }, [data]);
    return (
        <SmallFormGenerator
            initialValues={data}
            editMode={editMode}
            onSubmit={saveCheck}
            validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
            loading={loading}
            saving={saving || saveChecking}
            onCancel={onCancel}
            hideButtons={data?.request?.status === ERequestState.EXECUTED}
            formikInnerRef={(f) => (formikRef.current = f)}
            watchChanges={{myDogShowAuthorized: onDogShowUpdated}}
        >
            <FGListen
                field="clubId"
                onChanged={async (value, formik) => {
                    setClubId(value);
                    if (editMode) {
                        if (value !== data?.clubId) {
                            const email = await clubApi.getClubEmail(value);
                            formik.setFieldValue("clubEmail", email);
                            const address = await clubApi.getClubAddress(value);
                            formik.setFieldValue("clubAddress", address);
                            const remark = await clubApi.getClubRemark(value);
                            setclubRemark(+value !== +formik?.initialValues?.clubId ? remark : formik?.initialValues?.clubRemark);
                            checkClubBelium(value, formik);
                        }
                    }
                }}
            />
            <FGListen field="sectionId" onChanged={(value) => setSectionId(value)}/>
            <FGListen
                field="otherAddress"
                onChanged={(value, formik) => {
                    setOtherAdress(value);
                    if (value) {
                        setIsBelgium(true);
                        setbelgianClub(true);
                    }
                    if (value === data.otherAddress) {
                        return;
                    }
                    if (value) {
                        formik.setFieldValue("countryId", belgiumCountryId);
                        formik.setFieldTouched("countryId", false);
                    } else {
                        formik.setFieldValue("countryId", null);
                        formik.setFieldTouched("countryId", false);
                        setIsBelgium(false);
                    }
                }}
            />
            <FGListen field="activityId" onChanged={(value) => setActivityId(value)}/>
            <FGListen field="countryId" onChanged={checkIsBelgium}/>
            <FieldSet
                title={t(ETLCodes.RequestData)}
                rightElement={
                    competitionRequestId > 0 && (
                        <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp}/>
                    )
                }
            >
                <FieldGroup columns={2}>
                    <FGSRSHDateMaskInput
                        name="request.requestDate"
                        label={t(ETLCodes.RequestDate)}
                        requiredMark
                        readonly={!statusEditable || data?.request?.isBlocked}
                    />
                    <FGTextAreaInput
                        name="request.remark"
                        label={t(ETLCodes.Remark)}
                        readonly={data?.request?.status === ERequestState.EXECUTED}
                    />
                </FieldGroup>
                <FieldGroup columns={2}>
                    <FGSelectInput
                        name="clubId"
                        label={t(ETLCodes.Club)}
                        items={clubs}
                        loading={cloading}
                        onItemViewClick={(item) => openNewTab(`${ERoutes.club}/${item}/detail/view`)}
                        readonly={!statusEditable || data?.request?.isBlocked}
                        rightElement={
                            !!clubRemark ? (
                                <Popover2 content={<ToolTipContainer>{clubRemark}</ToolTipContainer>}
                                          interactionKind={"hover"}>
                                    <div style={{marginLeft: "0.5rem", marginRight: "0.5rem"}}>
                                        <Icon icon={"warning-sign"} intent={"danger"}
                                              style={{color: "#ac2f33 !important"}}/>
                                    </div>
                                </Popover2>
                            ) : null
                        }
                        info={
                            +competitionRequestId > 0 && (
                                <SharepointButton
                                    existsInShp={clubExistsInShp}
                                    loading={clubShpLoading}
                                    onClick={openOrCreateClubInShp}
                                />
                            )
                        }
                    />

                    {!myDogShowAuthorized &&
                        <FGCheckboxInput name="shouldDouble" label={t(ETLCodes.ShouldDoubleResults)}/>}
                </FieldGroup>
                <FieldGroup columns={2}>
                    <FGPeopleSearchInput
                        name="request.personId"
                        label={t(ETLCodes.Contact)}
                        clubId={clubId}
                        onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
                        readonly={!statusEditable || data?.request?.isBlocked}
                        requiredMark
                        showWarning={true}
                    />
                    {!myDogShowAuthorized && <FGCheckboxInput name="crufts" label={t(ETLCodes.Crufts)}/>}
                </FieldGroup>
                <FieldGroup columns={2}>
                    <FGTextInput
                        name="clubEmail"
                        label={t(ETLCodes.ClubEmail)}
                        readonly={!statusEditable || data?.request?.isBlocked}
                    />
                </FieldGroup>
                <FieldGroup columns={2}>
                    <FGCheckboxInput name="myDogShowAuthorized" label={t(ETLCodes.MyDogShowAuthorized)}
                                     disabled={!belgianClub}/>
                </FieldGroup>
                {myDogShowAuthorized && (
                    <FieldGroup columns={2} fieldsetProps={{title: t(ETLCodes.MyDogShowParameters)}}>
                        {/* <FGCheckboxInput name="myDogShowBilling" label={t(ETLCodes.MyDogShowBilling)} /> */}
                        <FGPeopleSearchInput name="personId" label={t(ETLCodes.CompetitionManager)} showWarning/>
                        {/* <FGMultiSuggestInput
              label={t(ETLCodes.AllowedVolunteerProfiles)}
              items={volunteerProfiles}
              loading={volunteerProfilesLoading}
              displayField={"label"}
              valueField={"value"}
              isFilterable={true}
              name="competitionVolunteerProfiles"
            /> */}
                        <FGCheckboxInput name="useTablet" label={t(ETLCodes.IsTablet)}/>
                    </FieldGroup>
                )}
            </FieldSet>

            <FieldSet
                title={t(ETLCodes.Competition)}
                rightElement={
                    data?.competitionId > 0 && (
                        <Button
                            text={t(ETLCodes.OpenCompetition)}
                            minimal
                            rightIcon={IconNames.SHARE}
                            onClick={() => openNewTab(`${ERoutes.competition}/${data?.competitionId}/detail/view`)}
                            loading={loading}
                        ></Button>
                    )
                }
            >
                <FieldGroup columns={2}>
                    <div>
                        <FGSRSHDateMaskInput
                            name="dateFrom"
                            label={t(ETLCodes.DateFrom)}
                            requiredMark
                            readonly={!statusEditable || data?.request?.isBlocked}
                        />
                        <FGSelectInput
                            name="sectionId"
                            label={t(ETLCodes.Section)}
                            items={sections}
                            loading={sloading}
                            onItemViewClick={(item) => openNewTab(`${ERoutes.section}/${item}/detail/view`)}
                            readonly={!statusEditable || data?.request?.isBlocked}
                            requiredMark
                        />
                        {statusEditable ? null : (
                            <FGSRSHDateMaskInput
                                name="catalogReceptionDate"
                                label={t(ETLCodes.CatalogReception)}
                                requiredMark={data?.request?.status === ERequestState.AWAITING_CATALOG}
                                readonly={data?.request?.status !== ERequestState.AWAITING_CATALOG}
                            />
                        )}
                        <FGSelectInput
                            name="activityId"
                            label={t(ETLCodes.Activity)}
                            items={activities}
                            loading={aloading}
                            onItemViewClick={(item) => openNewTab(`${ERoutes.activity}/${item}/detail/view`)}
                            readonly={!statusEditable || data?.request?.isBlocked}
                            requiredMark
                        />
                        {statusEditable ? (
                            <FGEmpty/>
                        ) : (
                            <FGNumberInput
                                name="numberParticipants"
                                label={t(ETLCodes.ParticipantsCount)}
                                requiredMark={data?.request?.status === ERequestState.AWAITING_CATALOG}
                                readonly={
                                    data?.request?.status !== ERequestState.AWAITING_CATALOG &&
                                    data?.request?.status !== ERequestState.AUTHORIZED
                                }
                            />
                        )}
                    </div>
                    <div>
                        <FGSRSHDateMaskInput
                            name="dateTo"
                            label={t(ETLCodes.DateTo)}
                            requiredMark
                            readonly={!statusEditable || data?.request?.isBlocked}
                        />
                        <FGTextInput
                            name="competitionName"
                            label={t(ETLCodes.CompetitionName)}
                            placeholder={data?.competitionNamePlaceHolder}
                        />
                    </div>
                </FieldGroup>
                <FieldGroup columns={2}>
                    <FGSelectInput
                        name="subActivityId"
                        label={t(ETLCodes.SubActivity)}
                        items={subActivities}
                        loading={saloading}
                        onItemViewClick={(item) => openNewTab(`${ERoutes.subActivity}/${item}/detail/view`)}
                        readonly={!statusEditable || data?.request?.isBlocked}
                        requiredMark
                    />
                    <FGTextInput
                        name="extra"
                        label={t(ETLCodes.Extra)}
                        readonly={!statusEditable || data?.request?.isBlocked}
                        maxLength={255}
                    />
                </FieldGroup>
                <FieldGroup columns={2}>
                    <FGTextInput name="clubAddress" label={t(ETLCodes.ClubAddress)} readonly/>
                </FieldGroup>
                <FieldGroup columns={2}>
                    <FGSRSHCheckboxInput
                        name="otherAddress"
                        label={t(ETLCodes.OtherAddress)}
                        readonly={
                            (!statusEditable || data?.request?.isBlocked) &&
                            data?.request?.status !== ERequestState.AUTHORIZED &&
                            data?.request?.status !== ERequestState.JUDGE_VALIDATION
                        }
                    />
                </FieldGroup>
                <FieldGroup columns={2}>
                    <FGCountrySelect
                        name="countryId"
                        label={t(ETLCodes.Country)}
                        onItemViewClick={(item) => openNewTab(`${ERoutes.country}/${item}/detail/view`)}
                        readonly={true}
                        visible={!!formikRef.current?.values?.otherAddress}
                        requiredMark
                        includeBelgium={true}
                    />
                </FieldGroup>
                <FieldGroup columns={2}>
                    {isBelgium ? (
                        <FGSelectInput
                            name="postalCodeId"
                            label={t(ETLCodes.City)}
                            items={postalCodes}
                            loading={pcloading}
                            readonly={
                                (!statusEditable || data?.request?.isBlocked) &&
                                data?.request?.status !== ERequestState.AUTHORIZED &&
                                data?.request?.status !== ERequestState.JUDGE_VALIDATION
                            }
                            visible={!!formikRef.current?.values?.otherAddress}
                            requiredMark
                        />
                    ) : (
                        <FGTextInput
                            name="foreignCity"
                            label={t(ETLCodes.City)}
                            readonly={
                                (!statusEditable || data?.request?.isBlocked) &&
                                data?.request?.status !== ERequestState.AUTHORIZED &&
                                data?.request?.status !== ERequestState.JUDGE_VALIDATION
                            }
                            visible={otherAdress}
                        />
                    )}
                </FieldGroup>
            </FieldSet>
            <FieldSet title={t(ETLCodes.Judges)}>
                <FGCustomPanel>
                    {({formik}) =>
                        formik?.values?.subActivityId ? (
                            <>
                                <CompetitionJudgeSelection
                                    linkedIds={formik?.values?.judgeIds || null}
                                    onLinkedIdsChanged={(ids) => {
                                        var uniqueIds = new Set(ids);
                                        formik?.setFieldValue("judgeIds", Array.from(uniqueIds));
                                    }}
                                    editMode={
                                        editMode &&
                                        !data?.request?.isBlocked &&
                                        (statusEditable ||
                                            data.request.status === ERequestState.JUDGE_VALIDATION ||
                                            data.request.status === ERequestState.CATALOG_JUDGE_VALIDATION ||
                                            data.request.status === ERequestState.AUTHORIZED)
                                    }
                                    loading={loading}
                                />
                            </>
                        ) : (
                            <NonIdealState icon="resolve"
                                           description={t(ETLCodes.PleaseSelectSubActivityToViewPossibleJudges)}/>
                        )
                    }
                </FGCustomPanel>
            </FieldSet>
            {data?.request?.status === ERequestState.EXECUTED && (
                <InlineButtonContainer>
                    <div/>
                    <ButtonsBloc style={{float: "right"}}>
                        <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.competitionRequest}`)}/>
                    </ButtonsBloc>
                </InlineButtonContainer>
            )}
        </SmallFormGenerator>
    );
};
